/**
 * A proposta como o cliente a vê: pelo link, sem conta e sem senha.
 *
 * Não passa por `ProtectedRoute` — quem está do outro lado não tem usuário no sistema, e exigir
 * um seria pedir que o prospecto se cadastre para ler a própria proposta. Quem responde pelo
 * acesso é o token de 32 bytes que veio no endereço.
 *
 * A abertura fica registrada no servidor, e é o que o comercial vê depois. Nada disso aparece
 * aqui: o corpo que a API pública devolve não traz contador nem observação interna.
 */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { Print as PrintIcon } from '@mui/icons-material';
import proposalService from '../../services/proposalService';
import { DocumentoDaProposta, FolhaEmBranco } from './components/DocumentoDaProposta';
import type { PropostaPublica } from '../../types/proposal';

export const ProposalPublicPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [proposta, setProposta] = useState<PropostaPublica | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    // Sem token não há o que buscar; a tela decide isso na renderização, e não com um setState
    // dentro do efeito — que dispara um segundo render só para dizer o que já se sabia.
    if (!token) return;
    let ativo = true;
    void (async () => {
      try {
        const p = await proposalService.publica(token);
        if (ativo) setProposta(p);
      } catch {
        // A mensagem é a mesma para link errado e para proposta apagada: dizer qual dos dois
        // foi contaria, a quem tentar adivinhar, se aquele endereço existe.
        if (ativo) setErro('Proposta não encontrada. Confira o link com quem o enviou.');
      }
    })();
    return () => {
      ativo = false;
    };
  }, [token]);

  const recusa = token ? erro : 'Link inválido. Confira o endereço com quem o enviou.';

  if (recusa) {
    return (
      <Box p={6} display="flex" justifyContent="center">
        <Box maxWidth={480}>
          <Alert severity="warning">{recusa}</Alert>
        </Box>
      </Box>
    );
  }

  if (!proposta) {
    return (
      <Box p={6} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <FolhaEmBranco>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        className="nao-imprime"
        sx={{ p: 2 }}
      >
        <Typography variant="body2" color="text.secondary">
          Proposta nº {proposta.number} · {proposta.clientName}
        </Typography>
        <Box flexGrow={1} />
        <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}>
          Salvar em PDF
        </Button>
      </Stack>

      <DocumentoDaProposta proposta={proposta} />
    </FolhaEmBranco>
  );
};

export default ProposalPublicPage;
